// types
import { Article, ArticleCategory, UpcomingFeature } from '../../../@types';

// i18n
import { useTranslation } from 'react-i18next';

// router
import { useParams, useSearchParams } from 'react-router-dom';

// mui
import { Container, Grid } from '@mui/material';

// layouts
import { Authenticated } from '../../layouts/Authenticated/Authenticated';

// ui
import { Breadcrumbs, BreadcrumbsProps } from '../../ui/Breadcrumbs/Breadcrumbs';
import { Title } from '../../ui/Title/Title';

// cards
import { CardArticle } from '../../cards/CardArticle/CardArticle';

// pages
import { Filters } from './Filters/Filters';
import { Links } from './Links/Links';
import { Sidebar } from './Sidebar/Sidebar';
import { Upcoming } from './Upcoming/Upcoming';

// utils
import { getArticles } from '../../../utils/articles';
import { translateCategory } from '../../../utils/translations';

// theme
import { theme } from '../../../theme';

export const PageFeatures = () => {
	// hooks
	const { t } = useTranslation();
	const { id } = useParams<{ id: string }>();
	const [searchParams] = useSearchParams();

	// params
	const paramCategory = searchParams.get('category') as ArticleCategory | null;

	// vars
	const articles = getArticles({ t });

	const articlesFiltered = (
		id ? (articles as Article[]).filter((a) => a.id === id) : (articles as Article[])
	).filter((a) => !paramCategory || a.categories.includes(paramCategory));
	const [articleFeatured, ...articlesRest] = articlesFiltered;
	const BreadcrumbsProps: BreadcrumbsProps = {
		crumbs: [],
		page: t('newFeatures'),
	};

	if (id || paramCategory) {
		BreadcrumbsProps.crumbs.push({
			children: t('newFeatures'),
			to: '/features',
		});

		if (articleFeatured) {
			BreadcrumbsProps.page = articleFeatured.title;
		}

		if (paramCategory) {
			BreadcrumbsProps.page = translateCategory(paramCategory, t);
		}
	}

	const featuresUpcoming: UpcomingFeature[] = [
		{
			description: t('articles.upcoming.contractWorkflow.description'),
			title: t('articles.upcoming.contractWorkflow.title'),
		},
		{
			description: t('articles.upcoming.supplierInvoices.description'),
			title: t('articles.upcoming.supplierInvoices.title'),
		},
		{
			description: t('articles.upcoming.withdrawalApplicationInitiation.description'),
			title: t('articles.upcoming.withdrawalApplicationInitiation.title'),
		},
	];

	return (
		<Authenticated>
			<Grid container spacing={2}>
				<Grid item xs={12}>
					<Breadcrumbs {...BreadcrumbsProps} />
				</Grid>
				<Grid item xs={12}>
					<Title title={t('newFeatures')} />
				</Grid>
				<Grid item xs={12}>
					<Container maxWidth={false}>
						<Grid container spacing={2}>
							<Grid item xs={12} lg={9}>
								<Grid container direction={{ xs: 'column-reverse', lg: 'row' }} spacing={2}>
									<Grid item xs={12} lg={4}>
										<Sidebar activeId={id} articles={articles as Article[]} />
									</Grid>
									<Grid item xs={12} lg={8}>
										<Grid container spacing={2} sx={{ height: `calc(100% + ${theme.spacing(2)})` }}>
											{articleFeatured && (
												<Grid item xs={12}>
													<CardArticle article={articleFeatured} isFeatured={!paramCategory} />
												</Grid>
											)}
											{articlesRest.map((article, i) => (
												<Grid item key={`article-${i}`} xs={12}>
													<CardArticle article={article} />
												</Grid>
											))}
										</Grid>
									</Grid>
								</Grid>
							</Grid>

							<Grid item xs={12} lg={3}>
								<Grid
									container
									direction="column"
									spacing={2}
									sx={{ height: `calc(100% + ${theme.spacing(2)})` }}>
									<Grid item>
										<Filters />
									</Grid>
									<Grid item>
										<Links />
									</Grid>
									<Grid flex={1} item>
										<Upcoming features={featuresUpcoming} />
									</Grid>
								</Grid>
							</Grid>
						</Grid>
					</Container>
				</Grid>
			</Grid>
		</Authenticated>
	);
};
